import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Camera, 
  Bell, 
  KeyRound, 
  FileText, 
  Calendar,
  Shield,
  ArrowRight,
  CheckCircle2,
  Users,
  Building2,
  TrendingUp,
  Clock,
  Star,
  Play,
  Check,
  Mail,
  Phone
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showDemoForm, setShowDemoForm] = useState(false);

  const features = [
    {
      icon: Camera,
      title: 'Caméras',
      description: 'Surveillance en temps réel avec gestion IP/NVR/DVR',
      color: 'blue',
      gradient: 'from-blue-500 to-blue-700'
    },
    {
      icon: Bell,
      title: 'Alarmes',
      description: 'Système d\'alarme intelligent et réactif',
      color: 'red',
      gradient: 'from-red-500 to-red-700'
    },
    {
      icon: KeyRound,
      title: 'Contrôle d\'accès',
      description: 'Badges QR Code et NFC pour une sécurité optimale',
      color: 'green',
      gradient: 'from-green-500 to-green-700'
    },
    {
      icon: FileText,
      title: 'Rapports',
      description: 'Génération PDF/Excel pour un suivi détaillé',
      color: 'purple',
      gradient: 'from-purple-500 to-purple-700'
    },
    {
      icon: Calendar,
      title: 'Planning Agents',
      description: 'Gestion des horaires et affectations par site',
      color: 'orange',
      gradient: 'from-orange-500 to-orange-700'
    }
  ];

  const stats = [
    { number: '50+', label: 'Sites sécurisés', icon: Building2 },
    { number: '200+', label: 'Agents gérés', icon: Users },
    { number: '99.9%', label: 'Disponibilité', icon: TrendingUp },
    { number: '24/7', label: 'Support actif', icon: Clock }
  ];

  const testimonials = [
    {
      name: 'Jean Dupont',
      company: 'LogiSec International',
      role: 'Directeur Sécurité',
      content: 'Une solution révolutionnaire qui a transformé notre gestion de la sécurité. Interface intuitive et équipe support exceptionnelle.',
      rating: 5
    },
    {
      name: 'Marie Martin',
      company: 'SecureGuard Europe',
      role: 'CEO',
      content: 'Le ROI a été immédiat. Nous avons réduit nos coûts opérationnels de 40% tout en améliorant notre efficacité.',
      rating: 5
    },
    {
      name: 'Ahmed Hassan',
      company: 'Global Protection Services',
      role: 'Operations Manager',
      content: 'La meilleure plateforme de gestion de sécurité du marché. Le planning automatique nous fait gagner des heures chaque semaine.',
      rating: 5
    }
  ];

  const pricing = [
    {
      name: 'Starter',
      price: '99',
      period: 'mois',
      description: 'Parfait pour débuter',
      features: [
        'Jusqu\'à 10 sites',
        '50 agents',
        '100 caméras',
        'Rapports basiques',
        'Support email'
      ]
    },
    {
      name: 'Professional',
      price: '299',
      period: 'mois',
      description: 'Pour les entreprises en croissance',
      features: [
        'Jusqu\'à 50 sites',
        '500 agents',
        '1000 caméras',
        'Rapports avancés',
        'Support prioritaire',
        'API access'
      ],
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Sur mesure',
      period: '',
      description: 'Solution personnalisée',
      features: [
        'Sites illimités',
        'Agents illimités',
        'Caméras illimitées',
        'Rapports personnalisés',
        'Support dédié 24/7',
        'Formation sur site',
        'Intégrations custom'
      ]
    }
  ];

  const benefits = [
    'Solution 100% adaptable à vos besoins',
    'De la gestion globale à l\'opérationnel',
    'Interface intuitive et moderne',
    'Support multi-sites et multi-agents',
    'Reporting en temps réel',
    'Mise en place rapide (48h)',
    'Formation incluse',
    'Mises à jour gratuites'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="container mx-auto px-6 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_0f1068d2-e428-4abf-9ea6-c10e13458ae4/artifacts/khbka7ja_te%CC%81le%CC%81chargement%20%2845%29.jpeg" 
              alt="SecurESSI-AI Logo" 
              className="w-16 h-16 object-contain rounded-lg"
            />
            <div>
              <h1 className="text-2xl font-bold text-white">SecurESSI-AI</h1>
              <p className="text-blue-300 text-sm">High Tech Security LTD • Secutor Pro LLC</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate('/register-company')} 
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
            >
              🏢 Inscription Société
            </Button>
            <Button 
              onClick={() => navigate('/register-admin')} 
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            >
              👤 Rejoindre
            </Button>
            <Button 
              onClick={() => navigate('/login')} 
              variant="outline" 
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            >
              Connexion
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-2xl mb-6 backdrop-blur">
            <img 
              src="https://customer-assets.emergentagent.com/job_0f1068d2-e428-4abf-9ea6-c10e13458ae4/artifacts/khbka7ja_te%CC%81le%CC%81chargement%20%2845%29.jpeg" 
              alt="SecurESSI-AI" 
              className="w-16 h-16 object-contain rounded-xl"
            />
          </div>
          
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">SecurESSI-AI</span>
            <br />
            Plateforme IA pour la{' '}
            <span className="text-blue-400">Sécurité Privée</span>
          </h2>
          
          <p className="text-xl text-gray-300 mb-4 leading-relaxed">
            🧬 <strong>ALFA Fédéré</strong> - 5 Moteurs IA • Mistral • Alpha-1 • Alpha-2 • Alpha-3 • Alpha-4
          </p>
          <p className="text-lg text-gray-400 mb-8">
            Architecture IA hybride de nouvelle génération avec mémoire persistante et garde-fous
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            <Button 
              onClick={() => navigate('/register-company')} 
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-6 text-lg"
            >
              🏢 Inscrire ma société
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              onClick={() => navigate('/register-admin')} 
              size="lg"
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-6 text-lg"
            >
              👤 Rejoindre une société
            </Button>
            <Button 
              onClick={() => navigate('/login')} 
              size="lg"
              variant="outline"
              className="bg-transparent text-white border-white/20 hover:bg-white/10 px-8 py-6 text-lg"
            >
              Se connecter
            </Button>
          </div>
          <div className="flex items-center justify-center gap-4">
            <Button 
              onClick={() => navigate('/pricing')} 
              size="lg"
              variant="outline"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20 px-8 py-6 text-lg"
            >
              💰 Voir les Tarifs
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all">
                <CardContent className="p-6 text-center">
                  <Icon className="w-8 h-8 text-blue-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white mb-1">{stat.number}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h3 className="text-3xl font-bold text-white mb-4">
            Une plateforme complète
          </h3>
          <p className="text-gray-400 text-lg">
            Tous les outils dont vous avez besoin en un seul endroit
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card 
                key={index}
                className="group bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20 hover:-translate-y-2"
              >
                <CardContent className="p-6 text-center">
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors">
                    {feature.title}
                  </h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="max-w-3xl mx-auto">
          <Card className="bg-white/5 border-white/10 backdrop-blur-sm">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">
                Pourquoi choisir notre solution ?
              </h3>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-gray-300 text-lg">{benefit}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="container mx-auto px-6 py-20 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl font-bold text-white mb-4">
              Ils nous font confiance
            </h3>
            <p className="text-gray-400 text-lg">
              Rejoignez des centaines d'entreprises satisfaites
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 italic leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="border-t border-white/10 pt-4">
                    <div className="font-bold text-white">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">{testimonial.role}</div>
                    <div className="text-sm text-blue-400">{testimonial.company}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h3 className="text-4xl font-bold text-white mb-4">
            Tarifs transparents et flexibles
          </h3>
          <p className="text-gray-400 text-lg">
            Choisissez le plan qui correspond à vos besoins
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {pricing.map((plan, index) => (
            <Card 
              key={index} 
              className={`bg-white/5 border-white/10 backdrop-blur-sm hover:bg-white/10 transition-all relative ${plan.popular ? 'ring-2 ring-blue-500 shadow-2xl shadow-blue-500/20' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-1 rounded-full text-sm font-bold">
                    Le plus populaire
                  </span>
                </div>
              )}
              <CardContent className="p-8">
                <h4 className="text-2xl font-bold text-white mb-2">{plan.name}</h4>
                <p className="text-gray-400 mb-6">{plan.description}</p>
                <div className="mb-6">
                  {plan.period ? (
                    <>
                      <span className="text-5xl font-bold text-white">€{plan.price}</span>
                      <span className="text-gray-400">/{plan.period}</span>
                    </>
                  ) : (
                    <span className="text-3xl font-bold text-white">{plan.price}</span>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full ${plan.popular ? 'bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800' : 'bg-white/10 hover:bg-white/20'}`}
                  onClick={() => setShowDemoForm(true)}
                >
                  {plan.price === 'Sur mesure' ? 'Nous contacter' : 'Commencer'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Demo Form Section */}
      {showDemoForm && (
        <section className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <Card className="bg-slate-900 border-white/10 max-w-2xl w-full">
            <CardContent className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">
                  Demander une démonstration
                </h3>
                <button onClick={() => setShowDemoForm(false)} className="text-gray-400 hover:text-white">
                  ✕
                </button>
              </div>
              <form className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Prénom"
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    placeholder="Nom"
                    className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <input
                  type="email"
                  placeholder="Email professionnel"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="tel"
                  placeholder="Téléphone"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="Entreprise"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option>Nombre de sites</option>
                  <option>1-10</option>
                  <option>11-50</option>
                  <option>51-100</option>
                  <option>100+</option>
                </select>
                <textarea
                  placeholder="Message (optionnel)"
                  rows="3"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Button className="w-full bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800">
                  Envoyer ma demande
                </Button>
              </form>
            </CardContent>
          </Card>
        </section>
      )}

      {/* CTA Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-bold text-white mb-6">
            Prêt à transformer votre gestion de sécurité ?
          </h3>
          <p className="text-xl text-gray-300 mb-8">
            Rejoignez les entreprises qui font confiance à High Tech Security
          </p>
          <Button 
            onClick={() => navigate('/register-company')} 
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-6 text-lg"
          >
            Essayer gratuitement
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-6 py-8 border-t border-white/10">
        <div className="flex flex-col md:flex-row items-center justify-between text-center md:text-left space-y-4 md:space-y-0">
          <div className="text-gray-400 text-sm">
            © 2024 High Tech Security Ltd. All rights reserved.
          </div>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-400">
            <span>+44 7591 665201 (UK)</span>
            <span>•</span>
            <span>+34 673 075445 (ES)</span>
            <span>•</span>
            <span>+33 6 58 63 59 08 (FR)</span>
            <span>•</span>
            <span>+1 302 579 0159 (US)</span>
          </div>
          <div className="text-sm text-gray-400">
            <a href="https://www.htsinvestigation.co.uk/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400">
              www.htsinvestigation.co.uk
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
